/**
 * Minimal RFC 4180 CSV reader/writer.
 *
 * Location descriptions are free-form prose, so quoted fields containing
 * commas, embedded newlines and escaped quotes are the normal case rather than
 * an edge case — a naive `split(',')` corrupts them silently.
 */

/**
 * Parses CSV text into rows of raw string cells. A leading UTF-8 BOM (Excel
 * writes one) is stripped so the first header does not gain an invisible prefix.
 */
export const parseCsv = (input: string): string[][] => {
  const text = input.charCodeAt(0) === 0xfeff ? input.slice(1) : input;
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let i = 0;

  while (i < text.length) {
    const char = text[i];

    if (inQuotes) {
      if (char === '"') {
        // A doubled quote inside a quoted field is a literal quote.
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += char;
      i += 1;
      continue;
    }

    if (char === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (char === ',') {
      row.push(field);
      field = '';
      i += 1;
      continue;
    }
    if (char === '\r') {
      i += 1;
      continue;
    }
    if (char === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      i += 1;
      continue;
    }
    field += char;
    i += 1;
  }

  // Flush the trailing record when the file does not end with a newline.
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  return rows;
};

const escapeCsvField = (value: string): string =>
  /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

/** Serializes rows back to CSV text using CRLF line endings. */
export const serializeCsv = (rows: string[][]): string =>
  rows.map((row) => row.map(escapeCsvField).join(',')).join('\r\n');

/**
 * Normalizes a header cell for comparison: lower-cased, with runs of
 * non-alphanumeric characters collapsed to single underscores. This lets
 * "Location Description", "location_description" and "LOCATION-DESCRIPTION" all
 * match, so the assistant does not break if the export's casing changes.
 */
export const normalizeHeader = (header: string): string =>
  header
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
